import * as React from "react";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Command as CommandPrimitive } from "cmdk";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type Options = {
  value: string;
  label: string;
};

interface FancyMultiSelectPopoverProps {
  value: Options[];
  onValueChange: (value: Options[]) => void;
  placeholder?: string;
  options: Options[];
  freeText?: boolean;
  expandOnFocus?: boolean;
  singleSelect?: boolean;
  maxLength?: number;
  maxItems?: number;
}

export const FancyMultiSelectPopover = React.forwardRef<HTMLDivElement, FancyMultiSelectPopoverProps>(function FancyMultiSelectPopover({
  value,
  onValueChange,
  placeholder,
  options,
  freeText = false,
  expandOnFocus = false,
  singleSelect = false,
  maxLength,
  maxItems,
}, ref) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  const handleUnselect = React.useCallback((option: Options) => {
    onValueChange(value.filter((s) => s.value !== option.value));
  }, [onValueChange, value]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (input) {
        if (e.key === "Delete" || e.key === "Backspace") {
          if (input.value === "") {
            onValueChange(value.slice(0, -1));
          }
        }
        if (e.key === "Escape") {
          input.blur();
          setOpen(false);
        }
      }
    },
    [onValueChange, value],
  );

  const sortedOptions = [...options].sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  function filterOptions(
    sortedOptions: Options[],
    value: Options[],
    inputValue: string = ""
  ): Options[] {
    return sortedOptions.filter(
      (option) => {
        const isNotSelected = !value.some((selected) => selected.value === option.value);
        if (inputValue) {
          return isNotSelected && option.label.toLowerCase().includes(inputValue.toLowerCase());
        }
        return isNotSelected;
      }
    );
  }

  const selectables = filterOptions(sortedOptions, value, inputValue);

  const handleAddFreeText = React.useCallback(() => {
    if (!inputValue) return;
    
    // Check if max items limit is reached
    if (maxItems && value.length >= maxItems && !singleSelect) {
      return;
    }
    
    // Check if input exceeds max length
    if (maxLength && inputValue.length > maxLength) {
      return;
    }
    
    // Check if the value already exists (case-insensitive comparison)
    const valueExists = value.some(
      (item) => item.label.toLowerCase() === inputValue.toLowerCase()
    );
    if (valueExists) {
      setInputValue("");
      return;
    }
    
    const newOption: Options = {
      value: inputValue,
      label: inputValue,
    };
    
    if (singleSelect) {
      onValueChange([newOption]);
      setOpen(false);
    } else {
      onValueChange([...value, newOption]);
    }
    setInputValue("");
  }, [inputValue, onValueChange, value, maxItems, maxLength, singleSelect]);

  const showAddNew = freeText && 
    inputValue.length > 0 &&
    !selectables.some(opt => opt.label.toLowerCase() === inputValue.toLowerCase()) &&
    !value.some(opt => opt.label.toLowerCase() === inputValue.toLowerCase());

  const shouldShowDropdown = open && (selectables.length > 0 || (freeText && inputValue.length > 0));

  return (
    <Command
      ref={ref}
      onKeyDown={handleKeyDown}
      className="overflow-visible bg-transparent"
      shouldFilter={false}
    >
      <Popover open={shouldShowDropdown} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="group rounded-md border border-input px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
            <div className="flex flex-wrap gap-1">
              {singleSelect && value.length > 0 ? (
                <div className="flex-1 flex items-center justify-between">
                  <span>{value[0].label}</span>
                  <button
                    className="ml-1 rounded-full outline-hidden ring-offset-background focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={() => {
                      onValueChange([]);
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              ) : (
                value.map((option) => (
                  <Badge key={option.value} variant="secondary">
                    {option.label}
                    <button
                      className="ml-1 rounded-full outline-hidden ring-offset-background focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleUnselect(option);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => handleUnselect(option)}
                    >
                      <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                    </button>
                  </Badge>
                ))
              )}
              <CommandPrimitive.Input
                ref={inputRef}
                value={inputValue}
                onValueChange={setInputValue}
                onBlur={(e) => {
                  // Check if the blur is happening because of interaction with the popover
                  const relatedTarget = e.relatedTarget as HTMLElement;
                  if (relatedTarget && relatedTarget.closest('[role="dialog"]')) {
                    // Don't close if clicking within the popover
                    return;
                  }
                  setOpen(false);
                }}
                onFocus={() => expandOnFocus && setOpen(true)}
                onClick={() => !expandOnFocus && setOpen(true)}
                placeholder={singleSelect && value.length > 0 ? "" : placeholder}
                className="flex-1 bg-transparent outline-hidden placeholder:text-muted-foreground"
                style={singleSelect && value.length > 0 ? { display: 'none' } : {}}
                maxLength={maxLength}
              />
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent 
          className="w-(--radix-popover-trigger-width) p-0" 
          align="start"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <CommandList>
            <CommandGroup className="max-h-72 overflow-auto">
              {selectables.map((option) => (
                <CommandItem
                  key={option.value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onSelect={() => {
                    // Check if max items limit is reached
                    if (!singleSelect && maxItems && value.length >= maxItems) {
                      return;
                    }
                    setInputValue("");
                    if (singleSelect) {
                      onValueChange([option]);
                      setOpen(false);
                    } else {
                      onValueChange([...value, option]);
                      // Focus back to the input after selection
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }
                  }}
                  className="cursor-pointer"
                >
                  {option.label}
                </CommandItem>
              ))}
              {showAddNew && (
                <CommandItem
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onSelect={() => {
                    handleAddFreeText();
                    if (!singleSelect) {
                      // Focus back to the input after selection
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }
                  }}
                  className="cursor-pointer flex items-center text-blue-600 hover:text-blue-700"
                >
                  <span className="mr-2">+</span>
                  Add "{inputValue}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </PopoverContent>
      </Popover>
    </Command>
  );
});

FancyMultiSelectPopover.displayName = "FancyMultiSelectPopover";